import { type ComponentType, useRef } from 'react';
import { ViewIds } from '@ardoq/api-types';
import { ViewModelStreamConfig } from './types';
import { combineLatest, map, shareReplay, withLatestFrom } from 'rxjs';
import { partitionByKeys } from '@ardoq/rxbeach';
import type { HasViewInstanceId } from '@ardoq/graph';
import { getWorkspaceModeViewSettingsWithChanges$ } from 'views/environmentals/viewSettings/workspaceModeViewSettings$';
import { connectRelationshipsRenderingDataLayer } from './connectRelationshipsRenderingDataLayer';
import { getWorkspaceModeSourceData$ } from './viewModel$/getWorkspaceModeSourceData$';
import {
  COLLAPSED_GROUP_IDS_PARTITION_KEYS,
  RENDERING_DATA_PARTITION_KEYS,
  SOURCE_DATA_PARTITION_KEYS,
  UI_PROPS_PARTITION_KEYS,
} from './viewModel$/viewSettingsPartitions';

const VIEW_ID = ViewIds.RELATIONSHIPS_3;

const getViewModelStreamConfig = ({
  viewInstanceId,
}: HasViewInstanceId): ViewModelStreamConfig => {
  const currentSettings$ = getWorkspaceModeViewSettingsWithChanges$(
    VIEW_ID,
    viewInstanceId
  ).pipe(
    map(({ currentSettings }) => currentSettings),
    shareReplay({ bufferSize: 1, refCount: true })
  );
  const [
    sourceDataSettings$,
    collapsedGroupIdsSettings$,
    renderingDataSettings$,
    uiPropsSettings$,
  ] = partitionByKeys(currentSettings$, [
    SOURCE_DATA_PARTITION_KEYS,
    COLLAPSED_GROUP_IDS_PARTITION_KEYS,
    RENDERING_DATA_PARTITION_KEYS,
    UI_PROPS_PARTITION_KEYS,
  ]);
  return {
    viewInstanceId,
    viewSettingsForModelSourceData$: sourceDataSettings$.pipe(
      withLatestFrom(collapsedGroupIdsSettings$),
      map(([source, collapsedGroupIds]) => ({
        ...source,
        ...collapsedGroupIds,
      }))
    ),
    viewSettingsForRenderingData$: combineLatest({
      collapsedGroupIds: collapsedGroupIdsSettings$,
      rendering: renderingDataSettings$,
    }).pipe(
      map(({ collapsedGroupIds, rendering }) => ({
        ...collapsedGroupIds,
        ...rendering,
      }))
    ),
    uiPropsSettings$,
  };
};

const WorkspaceModeRelationshipsView = (props: HasViewInstanceId) => {
  const connectedRef = useRef<ComponentType<object> | null>(null);
  if (!connectedRef.current) {
    connectedRef.current = connectRelationshipsRenderingDataLayer({
      getViewModelSourceData$: getWorkspaceModeSourceData$,
      viewModelStreamConfig: getViewModelStreamConfig(props),
      isViewpointMode: false,
    });
  }
  const ConnectedRelationshipsView = connectedRef.current;

  return <ConnectedRelationshipsView />;
};

export { WorkspaceModeRelationshipsView };
